import { waterFromMm } from './units';

const BAND_COLORS = {
  yellow: 'rgba(249, 168, 37, 0.22)',
  red: 'rgba(198, 40, 40, 0.32)',
};

/** Shades alert episodes (options.plugins.alertBands.bands) behind the datasets. */
export const alertBandsPlugin = {
  id: 'alertBands',
  beforeDatasetsDraw(chart, args, opts) {
    const bands = opts && opts.bands;
    const x = chart.scales.x;
    if (!bands || !bands.length || !x) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
    ctx.clip();
    for (const band of bands) {
      if (band.hold) continue;
      const color = BAND_COLORS[band.level];
      if (!color) continue;
      const x0 = x.getPixelForValue(band.startMs);
      const x1 = x.getPixelForValue(band.endMs);
      ctx.fillStyle = color;
      ctx.fillRect(x0, chartArea.top, Math.max(x1 - x0, 1), chartArea.bottom - chartArea.top);
    }
    ctx.restore();
  },
};

export const forecastShadePlugin = {
  id: 'forecastShade',
  beforeDatasetsDraw(chart, args, opts) {
    if (!opts || opts.now == null || opts.end == null) return;
    const x = chart.scales.x;
    const { ctx, chartArea } = chart;
    if (!x || !chartArea) return;
    const x0 = x.getPixelForValue(opts.now);
    const x1 = x.getPixelForValue(opts.end);
    const width = Math.max(x1 - x0, 0);
    ctx.save();
    ctx.fillStyle = 'rgba(31, 78, 121, 0.08)';
    ctx.fillRect(x0, chartArea.top, width, chartArea.bottom - chartArea.top);
    ctx.beginPath();
    ctx.strokeStyle = '#1F4E79';
    ctx.lineWidth = 1.5;
    ctx.moveTo(x0, chartArea.top);
    ctx.lineTo(x0, chartArea.bottom);
    ctx.stroke();
    ctx.font = '12px "IBM Plex Sans", sans-serif';
    ctx.fillStyle = '#1F4E79';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Now', x0, chartArea.top + 2);
    ctx.restore();
  },
};

export const levelZonesPlugin = {
  id: 'levelZones',
  beforeDatasetsDraw(chart, args, opts) {
    const y = chart.scales.y;
    const { chartArea, ctx } = chart;
    if (!opts || !y || !chartArea) return;
    const { left, right, top, bottom } = chartArea;
    const width = right - left;
    const yOf = (value) => y.getPixelForValue(value);
    const fill = (from, to, color) => {
      if (from == null || to == null) return;
      const y0 = Math.max(top, Math.min(from, to));
      const y1 = Math.min(bottom, Math.max(from, to));
      if (y1 <= y0) return;
      ctx.fillStyle = color;
      ctx.fillRect(left, y0, width, y1 - y0);
    };
    ctx.save();
    const watchY = opts.watch == null ? null : yOf(opts.watch);
    const floodY = opts.flood == null ? null : yOf(opts.flood);
    if (watchY != null && floodY != null) {
      fill(bottom, watchY, '#EAF3EC');
      fill(watchY, floodY, '#FBF2DA');
      fill(floodY, top, '#F8E3E1');
    } else if (watchY != null) {
      fill(bottom, watchY, '#EAF3EC');
      fill(watchY, top, '#FBF2DA');
    } else if (floodY != null) {
      fill(bottom, floodY, '#EAF3EC');
      fill(floodY, top, '#F8E3E1');
    }
    ctx.restore();
  },
};

export const thresholdLabelsPlugin = {
  id: 'thresholdLabels',
  afterDatasetsDraw(chart, args, opts) {
    const lines = opts?.lines || [];
    const y = chart.scales.y;
    const { ctx, chartArea } = chart;
    if (!y || !chartArea || !lines.length) return;
    ctx.save();
    ctx.font = '600 12px "IBM Plex Sans", sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    for (const line of lines) {
      const yPx = y.getPixelForValue(line.y);
      if (yPx < chartArea.top + 12 || yPx > chartArea.bottom) continue;
      const x = chartArea.right - 6;
      const yText = yPx - 2;
      ctx.strokeText(line.label, x, yText);
      ctx.fillStyle = '#1B1D1C';
      ctx.fillText(line.label, x, yText);
    }
    ctx.restore();
  },
};

export const forecastBandPlugin = {
  id: 'forecastBand',
  beforeDatasetsDraw(chart, args, opts) {
    const band = opts?.band;
    const x = chart.scales.x;
    const y = chart.scales.y;
    const { ctx, chartArea } = chart;
    if (!band || band.length < 2 || !x || !y || !chartArea) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.clip();
    ctx.beginPath();
    band.forEach((point, index) => {
      const px = x.getPixelForValue(point.t);
      const py = y.getPixelForValue(point.upper);
      if (index === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    for (let index = band.length - 1; index >= 0; index -= 1) {
      ctx.lineTo(x.getPixelForValue(band[index].t), y.getPixelForValue(band[index].lower));
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(31, 78, 121, 0.16)';
    ctx.fill();
    ctx.restore();
  },
};

export function displayBand(band, unitSystem) {
  if (!band) return null;
  return band.map((point) => ({
    t: point.t,
    lower: waterFromMm(point.lower, unitSystem),
    upper: waterFromMm(point.upper, unitSystem),
  }));
}
